import React, { useState, useEffect, useRef } from 'react';
import { Card, Input, Spinner } from 'reactstrap';
import { debounce } from 'lodash';
import { FaSearch } from 'react-icons/fa';
import notFound from '../../../../../../../../assets/images/search-not-found.svg';

const SearchSelectList = ({
  label,
  subLabel,
  placeholder = '',
  selectedItems = [],
  onSelect,
  searchApi, // (query) => Promise<data[]>
  renderItem, // (item) => JSX
  itemKey = 'id',
  name = '',
  setAdd,
  distanceType = '', // optional
  setDistanceType = null, // optional
  isAddNew = true,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const wrapperRef = useRef(null);

  const isSelected = item => selectedItems.some(i => i[itemKey] === item[itemKey]);

  const toggleSelect = item => {
    if (isSelected(item)) {
      onSelect(selectedItems.filter(i => i[itemKey] !== item[itemKey]));
    } else {
      onSelect([...selectedItems, item]);
    }
  };

  const debouncedSearch = debounce(async text => {
    setLoading(true);
    try {
      const data = await searchApi(text);
      setResults(data || []);
    } catch (err) {
      console.error('Search failed', err);
    }
    setLoading(false);
  }, 300);

  useEffect(() => {
    if (query || showPopup) {
      debouncedSearch(query);
    }
    return () => debouncedSearch.cancel();
  }, [query]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="position-relative border border-radius-5 p-3 mt-3 mb-3">
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <h6 className="fw-semibold fs-14">{label}</h6>
          <div className="text-muted mb-2 small">{subLabel}</div>
        </div>

        {distanceType && (
          <div>
            <p className="fs-10 mb-1">Distance Type</p>
            <div className="d-flex gap-2">
              {['Radial', 'Road', 'Polygon'].map(type => (
                <p
                  key={type}
                  style={{
                    borderRadius: '25px',
                    color: '#6F7594',
                    border: distanceType === type ? '1px solid #405189' : '1px solid #D0D2DC',
                    background: distanceType === type ? '#ABB9E8' : 'transparent',
                    padding: '1px 8px',
                  }}
                  onClick={() => setDistanceType(type)}
                  className="cursor-pointer fs-10 m-0"
                >
                  {type}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="position-relative" ref={wrapperRef}>
        <FaSearch
          className="position-absolute"
          style={{
            top: '50%',
            left: '10px',
            transform: 'translateY(-50%)',
            color: '#999',
            zIndex: 2,
          }}
        />
        <Input
          type="text"
          placeholder={placeholder}
          className="ps-5"
          value={query}
          onFocus={() => {
            setShowPopup(true);
            debouncedSearch(query);
          }}
          onChange={e => setQuery(e.target.value)}
        />

        {showPopup && (
          <div
            className="border rounded shadow-sm p-2 bg-white position-absolute"
            style={{ top: '100%', zIndex: 9999, width: '100%', maxHeight: '350px' }}
          >
            <p className="fw-medium fs-14">Search Result:</p>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center">
                <Spinner size="sm" />
              </div>
            ) : results.length > 0 ? (
              <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                {results.map(item => {
                  const status = item?.status?.toLowerCase();

                  let statusClass = 'custom-status-badge ';
                  if (status === 'live' || status === 'active') statusClass += 'custom-status-live';
                  else if (status === 'pending') statusClass += 'custom-status-pending';
                  else if (status === 'rejected') statusClass += 'custom-status-rejected';
                  else if (status === 'draft') statusClass += 'custom-status-draft';
                  else if (status === 'approved') statusClass += 'custom-status-approved';
                  else if (status === 'published') statusClass += 'custom-status-published';

                  return (
                    <div
                      key={item[itemKey]}
                      onClick={() => toggleSelect(item)}
                      className={`d-flex justify-content-between align-items-center p-2 mt-2 border-bottom ${
                        isSelected(item) ? 'bg-light border border-primary' : 'bg-white'
                      }`}
                      style={{ cursor: 'pointer' }}
                    >
                      {renderItem(item)}

                      <div className="d-flex align-items-center gap-2">
                        {/* status badge */}
                        {item?.status && (
                          <span style={{ minWidth: '56px', padding: '1px 5px' }} className={`${statusClass} fs-10`}>
                            {item?.status}
                          </span>
                        )}

                        {/* check icon placeholder to keep layout stable */}
                        <i
                          className="ri-check-line"
                          style={{
                            width: '20px',
                            visibility: isSelected(item) ? 'visible' : 'hidden',
                          }}
                        ></i>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="d-flex flex-column justify-content-center align-items-center">
                <img src={notFound} alt="" />
                <p className="fs-16 text-center fs-medium">No results found</p>
              </div>
            )}
            {isAddNew && (
              <div
                className="p-2 add-airport-zone-vendor m-2 cursor-pointer"
                onClick={() => {
                  setShowPopup(false);
                  setAdd(true);
                }}
              >
                <p className="m-0">
                  <i className="ri-add-line text-opacity-75 fs-14 align-middle px-1"></i>
                  Add New
                </p>
                <p className="ps-3 fs-10 m-0" style={{ color: '#586DB1' }}>
                  Click to add new {name}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchSelectList;
